import { forwardRef, ButtonHTMLAttributes } from 'react'
import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'secondary' | 'ghost' | 'destructive' | 'outline'
  size?: 'default' | 'sm' | 'lg' | 'icon'
}

const buttonVariants = {
  default: 'bg-white text-zinc-900 hover:bg-zinc-100',
  secondary: 'bg-zinc-800 text-white hover:bg-zinc-700',
  ghost: 'bg-transparent hover:bg-zinc-800 text-zinc-400 hover:text-white',
  destructive: 'bg-red-600 text-white hover:bg-red-700',
  outline: 'border border-zinc-700 bg-transparent hover:bg-zinc-800 text-white',
}

const buttonSizes = {
  default: 'h-10 px-4 py-2',
  sm: 'h-8 px-3 text-sm',
  lg: 'h-12 px-6 text-lg',
  icon: 'h-10 w-10',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'default', size = 'default', disabled, ...props }, ref) => {
    return (
      <button
        className={twMerge(
          clsx(
            'inline-flex items-center justify-center rounded-lg font-medium transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-500 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950',
            'disabled:pointer-events-none disabled:opacity-50',
            buttonVariants[variant],
            buttonSizes[size],
            className
          )
        )}
        ref={ref}
        disabled={disabled}
        {...props}
      />
    )
  }
)

Button.displayName = 'Button'
